import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialMessage, userMessage } from 'constants/ui.constants';
import {
	getHyperMonitoringAnalytics,
	getHyperMonitoringBeneficiaryDetails,
	getHyperMonitoringOperations,
	markHyperMonitoringOperationDocumented,
	resendHyperMonitoringOperation,
} from 'services/hyper-monitoring.service';
import type {
	HyperMonitoringAnalytics,
	HyperMonitoringOperationDetails,
	HyperMonitoringOperationRecord,
} from 'types/hyper-monitoring.types';

type HyperMonitoringRequestsState = {
	data: HyperMonitoringOperationRecord[];
	pagination: { total: number; hasMore: boolean };
	loading: boolean;
};

type HyperMonitoringDetailsState = {
	data: HyperMonitoringOperationDetails | null;
	loading: boolean;
	shouldCloseAfterFailure: boolean;
	operationId: string | null;
};

interface IHyperMonitoringState {
	analytics: {
		data: HyperMonitoringAnalytics;
		loading: boolean;
	};
	requests: HyperMonitoringRequestsState;
	details: HyperMonitoringDetailsState;
	actionLoadingById: Record<string, boolean>;
	error: userMessage;
}

const defaultAnalytics: HyperMonitoringAnalytics = {
	beneficiariesRequests: { total: 0, today: 0 },
};

const defaultRequests: HyperMonitoringRequestsState = {
	data: [],
	pagination: { total: 0, hasMore: false },
	loading: false,
};

const defaultDetailsState: HyperMonitoringDetailsState = {
	data: null,
	loading: false,
	shouldCloseAfterFailure: false,
	operationId: null,
};

const initialState: IHyperMonitoringState = {
	analytics: {
		data: defaultAnalytics,
		loading: false,
	},
	requests: { ...defaultRequests },
	details: { ...defaultDetailsState },
	actionLoadingById: {},
	error: initialMessage,
};

const hyperMonitoringSlice = createSlice({
	name: 'hyperMonitoring',
	initialState,
	reducers: {
		setError: (state, action: PayloadAction<userMessage>) => {
			state.error = action.payload;
		},
		clearError: (state) => {
			state.error = initialMessage;
		},
		clearRequests: (state) => {
			state.requests = { ...defaultRequests };
		},
		clearDetails: (state) => {
			state.details = { ...defaultDetailsState };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getHyperMonitoringAnalytics.pending, (state) => {
				state.analytics.loading = true;
			})
			.addCase(getHyperMonitoringAnalytics.fulfilled, (state, { payload }) => {
				state.analytics.loading = false;
				state.analytics.data = {
					beneficiariesRequests: payload.beneficiariesRequests,
				};
			})
			.addCase(getHyperMonitoringAnalytics.rejected, (state) => {
				state.analytics.loading = false;
			})
			.addCase(getHyperMonitoringOperations.pending, (state) => {
				state.requests.loading = true;
			})
			.addCase(getHyperMonitoringOperations.fulfilled, (state, { payload }) => {
				state.requests = {
					data: payload.result || [],
					pagination: payload.pagination || { total: 0, hasMore: false },
					loading: false,
				};
			})
			.addCase(getHyperMonitoringOperations.rejected, (state) => {
				state.requests.loading = false;
			})
			.addCase(getHyperMonitoringBeneficiaryDetails.pending, (state, { meta }) => {
				state.details.loading = true;
				state.details.shouldCloseAfterFailure = false;
				state.details.operationId = meta.arg.operationId;
				state.details.data = null;
			})
			.addCase(getHyperMonitoringBeneficiaryDetails.fulfilled, (state, { meta, payload }) => {
				if (state.details.operationId !== meta.arg.operationId) {
					return;
				}
				state.details.loading = false;
				state.details.data = payload;
				state.details.shouldCloseAfterFailure = false;
			})
			.addCase(getHyperMonitoringBeneficiaryDetails.rejected, (state, { meta }) => {
				if (state.details.operationId !== meta.arg.operationId) {
					return;
				}
				state.details.loading = false;
				state.details.data = null;
				state.details.shouldCloseAfterFailure = true;
			})
			.addCase(resendHyperMonitoringOperation.pending, (state, { meta }) => {
				state.actionLoadingById[meta.arg] = true;
			})
			.addCase(resendHyperMonitoringOperation.fulfilled, (state, { meta }) => {
				delete state.actionLoadingById[meta.arg];
			})
			.addCase(resendHyperMonitoringOperation.rejected, (state, { meta }) => {
				delete state.actionLoadingById[meta.arg];
			})
			.addCase(markHyperMonitoringOperationDocumented.pending, (state, { meta }) => {
				state.actionLoadingById[meta.arg] = true;
			})
			.addCase(markHyperMonitoringOperationDocumented.fulfilled, (state, { meta }) => {
				delete state.actionLoadingById[meta.arg];
			})
			.addCase(markHyperMonitoringOperationDocumented.rejected, (state, { meta }) => {
				delete state.actionLoadingById[meta.arg];
			});
	},
});

export const getHyperMonitoringAnalyticsData = (state: any) => state.hyperMonitoring.analytics;

export const getHyperMonitoringOperationsData = (state: any) => state.hyperMonitoring.requests;

export const getHyperMonitoringDetailsState = (state: any) => state.hyperMonitoring.details;

export const getHyperMonitoringError = (state: any) => state.hyperMonitoring.error;

export const getHyperMonitoringActionLoadingById = (state: any) => state.hyperMonitoring.actionLoadingById;

export const { setError, clearError, clearRequests, clearDetails } = hyperMonitoringSlice.actions;

export default hyperMonitoringSlice.reducer;
